import React from 'react';
import type {MutableRefObject} from 'react';
import type {AIMessageGeneratorPromptAreaProps, ChatAskPrompt} from '../../../aiMessageGenerator/utils/types';
import {toAskAnswer} from '../../utils/askOptionAnswer';
import AiAgentAsk from '../aiAgentAsk/AiAgentAsk';
import AiAgentInput from '../aiAgentInput/AiAgentInput';
import * as css from './AiAgentPromptArea.css';

interface AiAgentPromptAreaProps extends AIMessageGeneratorPromptAreaProps {
    pendingAsk: ChatAskPrompt | null;
    onClearAsk: () => void;
    openFilePickerRef?: MutableRefObject<() => void>;
    /** The tapped suggestion's explanation — see AiAgentSuggestions. */
    promptHint?: string | null;
    onClearPromptHint?: () => void;
}

function AiAgentPromptArea({
    pendingAsk,
    onClearAsk,
    openFilePickerRef,
    promptHint,
    onClearPromptHint,
    ...inputProps
}: AiAgentPromptAreaProps) {
    const {draftLoading, onSubmitPrompt, onPromptChange} = inputProps;

    // Filling from a suggestion sets the text directly, so only real typing lands here —
    // and once the user writes their own wording, the suggestion's explanation is stale.
    const handlePromptChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        onClearPromptHint?.();
        onPromptChange(event);
    };

    const handleSelect = (value: string, label?: string) => {
        onSubmitPrompt({
            text: toAskAnswer(value, pendingAsk),
            displayText: label || value,
        });
    };

    if(pendingAsk) {
        return (
            <AiAgentAsk
                key={pendingAsk.id ?? pendingAsk.question}
                question={pendingAsk.question}
                options={pendingAsk.options}
                draftLoading={draftLoading}
                onSelect={handleSelect}
                onDismiss={onClearAsk}
            />
        );
    }

    return (
        <>
            {!!promptHint && (
                <css.promptHint data-cy="ai-agent-prompt-hint">{promptHint}</css.promptHint>
            )}
            <AiAgentInput
                {...inputProps}
                onPromptChange={handlePromptChange}
                openFilePickerRef={openFilePickerRef}
            />
        </>
    );
}

export default AiAgentPromptArea;
